import { createFileRoute, redirect, Link, useRouter } from '@tanstack/react-router'
import { z } from 'zod'
import { cn } from '~/shared/common/utils'
import { sanitizeRedirectPath } from '~/shared/common/redirect-path'
import { Button, buttonVariants } from '~/client/components/ui/button'
import { Card, CardContent } from '~/client/components/ui/card'
import { Input } from '~/client/components/ui/input'
import { Label } from '~/client/components/ui/label'
import { toast } from 'sonner'
import { useForm } from '@tanstack/react-form'
import { useAuthStore } from '~/client/common/stores/auth'
import { RevelStreetLogoHeaderIcon } from '~/client/components/icons'
import { capture, PostHogEvent } from '~/client/common/posthog'

const LoginSearch = z.object({
  redirect: z.string().optional().catch(undefined),
})

export const Route = createFileRoute('/login')({
  validateSearch: LoginSearch,
  beforeLoad: async ({ search }) => {
    await useAuthStore.getState().initializeAuth()
    if (useAuthStore.getState().user) {
      const safeRedirect = sanitizeRedirectPath(search.redirect) ?? '/'
      throw redirect({ href: safeRedirect })
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const router = useRouter()
  const { redirect: rawRedirect } = Route.useSearch()
  const safeRedirect = sanitizeRedirectPath(rawRedirect) ?? '/'

  type LoginValues = { email: string; password: string }
  const defaultValues: LoginValues = { email: '', password: '' }

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      capture(PostHogEvent.LOGIN_SUBMITTED)
      try {
        await useAuthStore.getState().login(value.email, value.password)
        router.history.push(safeRedirect)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Login failed'
        toast.error(message, {
          position: 'bottom-right',
          richColors: true,
        })
      }
    },
  })

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-sm">
        <div className={cn('flex flex-col gap-6')}>
          <Card className="overflow-hidden p-0">
            <CardContent className="grid p-0 md:grid-cols-1">
              <form
                className="p-6 md:p-8"
                onSubmit={(e) => {
                  e.preventDefault()
                  form.handleSubmit()
                }}
              >
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col items-center text-center">
                    {/* <h1 className="text-2xl font-bold">Welcome back</h1>
                    <p className="text-muted-foreground text-balance">Login to your Acme Inc account</p> */}
                    <RevelStreetLogoHeaderIcon className="h-16 w-auto text-[#18003D]" />
                    <br />
                    <p className="text-muted-foreground text-balance">Login to your Revel Street account</p>
                  </div>
                  <div className="grid gap-3">
                    <Label htmlFor="email">Email</Label>
                    <form.Field
                      name="email"
                      validators={{
                        onChange: ({ value }) => (!value ? 'Email is required' : /\S+@\S+\.\S+/.test(value) ? undefined : 'Enter a valid email'),
                      }}
                      children={(field) => (
                        <>
                          <Input
                            id={field.name}
                            type="email"
                            placeholder="m@example.com"
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                            aria-invalid={field.state.meta.errors.length ? 'true' : 'false'}
                          />
                          {field.state.meta.errors[0] ? <span className="text-sm text-destructive">{field.state.meta.errors[0]}</span> : null}
                        </>
                      )}
                    />
                  </div>
                  <div className="grid gap-3">
                    <div className="flex items-center">
                      <Label htmlFor="password">Password</Label>
                      <Link to="/forgot-password" className="ml-auto text-sm underline-offset-2 hover:underline">
                        Forgot your password?
                      </Link>
                    </div>
                    <form.Field
                      name="password"
                      validators={{
                        onChange: ({ value }) => (!value ? 'Password is required' : undefined),
                      }}
                      children={(field) => (
                        <>
                          <Input
                            id={field.name}
                            type="password"
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(e) => field.handleChange(e.target.value)}
                            aria-invalid={field.state.meta.errors.length ? 'true' : 'false'}
                          />
                          {field.state.meta.errors[0] ? <span className="text-sm text-destructive">{field.state.meta.errors[0]}</span> : null}
                        </>
                      )}
                    />
                  </div>
                  <form.Subscribe
                    selector={(state) => [state.canSubmit, state.isSubmitting] as const}
                    children={([canSubmit, isSubmitting]) => (
                      <Button type="submit" className="w-full bg-[#8A3DFF] hover:bg-[#7B2FE6]" disabled={!canSubmit || isSubmitting}>
                        {isSubmitting ? 'Logging in...' : 'Login'}
                      </Button>
                    )}
                  />
                  <div className="text-muted-foreground text-center text-xs text-balance">
                    By continuing, you agree to our{' '}
                    <a href="https://revelstreet.co/terms" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-primary">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="https://revelstreet.co/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-primary">
                      Privacy Policy
                    </a>
                    .
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
          <div className="flex flex-col gap-4 text-center text-sm">
            <div>
              Company recently joined?{' '}
              <Link to="/activate" className="font-semibold underline underline-offset-4">
                Activate your account
              </Link>
            </div>
            <div className="flex items-center justify-center gap-3">
              <span>Not a customer?</span>
              <a href="https://www.revelstreet.co/demo" target="_blank" rel="noopener noreferrer" className={cn(buttonVariants({ variant: 'revelOutline', size: 'sm' }), 'text-sm')}>
                Request a Demo
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
